/**
 * The email on the 1st: last month's workers comp and general liability on
 * the crews that were paid, and the liability year so far (ADR 0275).
 *
 * The figures are `insuranceOwed.ts`'s; this only says them. It is sent by
 * `supabase/functions/insurance-email`, which carries a transcription of both
 * files and is held to them character for character by
 * `supabase/test/insurance-email-match.test.ts`. Change one, change both.
 */

import {
  INSURED_SUB_GL_PER_1000,
  LIABILITY_CLASSES,
  tradeList,
  wcClassForTrades,
  type InsuranceStanding,
} from "./insurance.js";
import {
  insuranceOwedByMonth,
  liabilityYear,
  type LiabilityYear,
  type OwedMonth,
  type OwedOrder,
  type PaidWorkOrder,
} from "./insuranceOwed.js";
import type { Cents } from "./money.js";

/** The month the email on the 1st is about: the one that just ended. */
export function previousMonth(today: string): string {
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  return month === 1
    ? `${year - 1}-12`
    : `${year}-${String(month - 1).padStart(2, "0")}`;
}

/** What the monthly email says. */
export interface InsuranceLetter {
  /** The month the comp is paid for, even when nobody was paid in it. */
  month: OwedMonth;
  year: LiabilityYear;
}

const emptyMonth = (month: string): OwedMonth => ({
  month,
  paid: 0 as Cents,
  workersComp: { ownCover: 0 as Cents, ours: 0 as Cents, owed: 0 as Cents },
  generalLiability: {
    ownCover: 0 as Cents,
    ours: 0 as Cents,
    owed: 0 as Cents,
  },
  crews: [],
});

export function insuranceLetter(
  orders: readonly PaidWorkOrder[],
  today: string,
): InsuranceLetter {
  const month = previousMonth(today);
  return {
    month:
      insuranceOwedByMonth(orders, today).find((m) => m.month === month) ??
      emptyMonth(month),
    year: liabilityYear(orders, today),
  };
}

/** "September 2026". */
export function monthName(month: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(new Date(`${month}-01T00:00:00Z`));
}

/** "Sep 2, 2026". */
function shortDate(day: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${day}T00:00:00Z`));
}

/** "$1,234.56", the same from every machine (ADR 0122). */
const money = (value: Cents): string => {
  const formatted = (Math.abs(value) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `-$${formatted}` : `$${formatted}`;
};

/** A crew as the email names it: "Apex Metal (Brad Kiley)". */
export function owedCrewName(crew: PaidWorkOrder["crew"]): string {
  const company = (crew?.name ?? "").trim();
  if (company === "") return "No crew chosen";
  const contact = (crew?.contact_name ?? "").trim();
  if (contact === "" || contact.toLowerCase() === company.toLowerCase()) {
    return company;
  }
  return `${company} (${contact})`;
}

/** Where a crew stands on one policy, in words. */
export function standingWords(
  standing: InsuranceStanding,
  policy: "comp" | "liability",
): string {
  if (standing === "insured")
    return policy === "comp" ? "has own comp" : "has own liability";
  if (standing === "uninsured")
    return policy === "comp" ? "no comp" : "no liability";
  return "no certificate on file";
}

/** The job a work order belongs to: "Job 1077 · Jane Arquette". */
export function owedJobLabel(order: PaidWorkOrder): string {
  const number = order.job?.job_number;
  const name = (order.job?.contact?.full_name ?? "").trim();
  const job = number == null ? "Job" : `Job ${number}`;
  return name === "" ? job : `${job} · ${name}`;
}

/** The trades on the work order, as typed on it: "Siding Repairs". */
export function owedTrades(order: PaidWorkOrder): string {
  const list = tradeList(order.trades);
  return list.length === 0 ? "No trade on the work order" : list.join(", ");
}

/**
 * Where the comp rate came from, when it is not the liability class's own:
 * " (Roofing - residential rate)". The class is the dearest on liability and
 * the comp rate the dearest on comp, which are not always the same class, and
 * roofing stands in where the fund gave none (issue #919).
 */
function compFrom(row: OwedOrder): string {
  const compClass = wcClassForTrades(row.order.trades);
  return compClass.code === row.insurance.klass.code
    ? ""
    : ` (${compClass.name} rate)`;
}

/**
 * The trade, the class it is rated in, and what each policy comes to at that
 * class's rate — every trade has its own, so every payout says which:
 * "Siding Repairs, rated as Roofing - residential: comp 7.35% = $220.50 ·
 * liability $125.35 per $1,000 = $376.05".
 */
export function owedRateLine(row: OwedOrder): string {
  const { insurance } = row;
  const comp =
    insurance.wcStanding === "insured"
      ? "comp $0.00 (has own comp)"
      : `comp ${insurance.wcPer100}%${compFrom(row)} = ${money(
          insurance.workersComp,
        )}`;
  const liability =
    `liability $${insurance.glPer1000.toFixed(2)} per $1,000 = ` +
    `${money(insurance.generalLiability)}` +
    (insurance.glStanding === "insured" ? " (has own liability)" : "");
  return `${owedTrades(row.order)}, rated as ${insurance.klass.name}: ${comp} · ${liability}`;
}

/**
 * How the month's liability is worked out, rate by rate. Jonathon,
 * 2026-10-13: "when you send the email make sure you list how you came up
 * with each number" (ADR 0275).
 */
export function liabilityWorking(): string[] {
  const rate = (value: number) => `$${value.toFixed(2)}`;
  return [
    "Each work order is charged per $1,000 of what the crew was paid on it:",
    `Crews with their own liability: ${rate(INSURED_SUB_GL_PER_1000)}`,
    ...LIABILITY_CLASSES.map((k) => `${k.name}: ${rate(k.glPer1000)}`),
    "The month's liability is those work orders added up. Each one is " +
      "listed under its crew with its rate and what it comes to.",
  ];
}

/** How far through the policy year today is, or that it has ended. */
export function yearProgress(year: LiabilityYear): string {
  return year.ended ? "ended" : `${Math.round(year.elapsed * 100)}% through`;
}

/** What the year is heading for, against what the policy was priced on. */
export function paceSentence(year: LiabilityYear): string {
  if (year.ended) {
    const against =
      year.paid > year.pricedOn
        ? `${money((year.paid - year.pricedOn) as Cents)} more than the ` +
          `${money(year.pricedOn)} it was priced on. Expect the audit to bill ` +
          "liability on the difference."
        : `within the ${money(year.pricedOn)} it was priced on.`;
    return (
      `The policy year on file ended on ${shortDate(year.to)}. Crews were ` +
      `paid ${money(year.paid)} in it, ${against} Until the renewal is ` +
      "entered in Roof Board, these figures are for the year that ended."
    );
  }
  if (year.paidOnPace == null) {
    return "The policy year has not started yet.";
  }
  if (year.paidOnPace > year.pricedOn) {
    return (
      `At this pace crews are paid ${money(year.paidOnPace)} this policy ` +
      `year, ${money((year.paidOnPace - year.pricedOn) as Cents)} more than ` +
      `the ${money(year.pricedOn)} the policy was priced on. Expect the ` +
      "audit to bill liability on the difference."
    );
  }
  return (
    `At this pace crews are paid ${money(year.paidOnPace)} this policy ` +
    `year, within the ${money(year.pricedOn)} the policy was priced on.`
  );
}

export interface InsuranceEmail {
  subject: string;
  text: string;
  html: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The email on the 1st, in both parts.
 *
 * It goes out on a month nobody was paid too, saying so: an email that only
 * comes when there is money to pay cannot be told apart from one that has
 * stopped working.
 */
export function insuranceEmail(
  letter: InsuranceLetter,
  options: { url: string },
): InsuranceEmail {
  const { month, year } = letter;
  const name = monthName(month.month);
  const wc = month.workersComp;
  const gl = month.generalLiability;

  const subject =
    `Insurance for ${name} — ${money(wc.owed)} workers comp, ` +
    `${money(gl.owed)} liability`;

  const text: string[] = [
    `Workers comp and general liability for ${name}, on the crews paid that month.`,
    "",
  ];
  if (month.paid === 0) {
    text.push(
      `No crews were paid in ${name}. Nothing is owed for the month.`,
      "",
    );
  } else {
    text.push(
      `WORKERS COMP — ${money(wc.owed)} to pay for ${name}`,
      `  Paid to crews: ${money(month.paid)}`,
      `  To crews with their own workers comp: ${money(wc.ownCover)} (nothing owed)`,
      `  To crews without it: ${money(wc.ours)} (comp is owed on this)`,
      "",
      `GENERAL LIABILITY — ${money(gl.owed)} for ${name}`,
      `  To crews with their own liability: ${money(gl.ownCover)} ($${INSURED_SUB_GL_PER_1000.toFixed(2)} per $1,000)`,
      `  To crews without it: ${money(gl.ours)} (the trade's full rate)`,
      "",
      "HOW THE LIABILITY IS WORKED OUT",
      ...liabilityWorking().map((line) => `  ${line}`),
      "",
    );
  }
  text.push(
    `LIABILITY POLICY YEAR — ${shortDate(year.from)} to ${shortDate(year.to)}, ${yearProgress(year)}`,
    `  Paid to crews so far: ${money(year.paid)} (${money(year.paidOwnCover)} to crews with their own liability)`,
    `  Liability so far: ${money(year.owed)}${
      year.owedOnPace == null ? "" : `, on pace for ${money(year.owedOnPace)}`
    }`,
    `  ${paceSentence(year)}`,
    "",
  );

  if (month.crews.length > 0) {
    text.push(`BY CREW — ${name}`);
    for (const crew of month.crews) {
      text.push(
        `  ${owedCrewName(crew.crew)} — paid ${money(crew.paid)}`,
        `     comp ${money(crew.workersComp)} (${standingWords(
          crew.wcStanding,
          "comp",
        )}) · liability ${money(crew.generalLiability)} (${standingWords(
          crew.glStanding,
          "liability",
        )})`,
      );
      for (const row of crew.orders) {
        text.push(
          `     WO-${row.order.order_number ?? "?"} · ${owedJobLabel(
            row.order,
          )} · paid ${shortDate(row.paidOn)} · ${money(row.paid)}`,
          `        ${owedRateLine(row)}`,
        );
      }
    }
    text.push("");
  }

  text.push(
    "Certificates are read as they stand today, so a crew whose certificate " +
      "has run out is charged as having none.",
    "",
    `Open it in Roof Board: ${options.url}`,
  );

  return {
    subject,
    text: text.join("\n"),
    html: insuranceHtml(letter, options),
  };
}

/** The same email as a page: inline styles only, read in Gmail on a phone. */
function insuranceHtml(
  letter: InsuranceLetter,
  options: { url: string },
): string {
  const { month, year } = letter;
  const name = escapeHtml(monthName(month.month));
  const wc = month.workersComp;
  const gl = month.generalLiability;
  const out: string[] = [];
  const p = (body: string) => out.push(`<p style="margin:0 0 6px">${body}</p>`);
  const h = (body: string) =>
    out.push(
      '<h3 style="margin:20px 0 6px;font-size:15px;border-bottom:1px solid #d8d8d8;' +
        `padding-bottom:4px">${body}</h3>`,
    );

  out.push(
    '<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;' +
      'font-size:14px;line-height:1.5;color:#1a1a1a;max-width:720px">',
  );
  p(
    `Workers comp and general liability for <b>${name}</b>, on the crews paid that month.`,
  );

  if (month.paid === 0) {
    p(`No crews were paid in ${name}. Nothing is owed for the month.`);
  } else {
    h(`Workers comp — ${escapeHtml(money(wc.owed))} to pay`);
    p(`Paid to crews: <b>${escapeHtml(money(month.paid))}</b>`);
    p(
      `To crews with their own workers comp: ${escapeHtml(
        money(wc.ownCover),
      )} <span style="color:#666">(nothing owed)</span>`,
    );
    p(
      `To crews without it: <b>${escapeHtml(
        money(wc.ours),
      )}</b> <span style="color:#666">(comp is owed on this)</span>`,
    );
    h(`General liability — ${escapeHtml(money(gl.owed))}`);
    p(
      `To crews with their own liability: ${escapeHtml(
        money(gl.ownCover),
      )} <span style="color:#666">($${INSURED_SUB_GL_PER_1000.toFixed(2)} per $1,000)</span>`,
    );
    p(
      `To crews without it: <b>${escapeHtml(
        money(gl.ours),
      )}</b> <span style="color:#666">(the trade's full rate)</span>`,
    );
    h("How the liability is worked out");
    for (const line of liabilityWorking()) p(escapeHtml(line));
  }

  h(
    `Liability policy year — ${escapeHtml(shortDate(year.from))} to ${escapeHtml(
      shortDate(year.to),
    )}, ${yearProgress(year)}`,
  );
  p(
    `Paid to crews so far: <b>${escapeHtml(money(year.paid))}</b> ` +
      `<span style="color:#666">(${escapeHtml(
        money(year.paidOwnCover),
      )} to crews with their own liability)</span>`,
  );
  p(
    `Liability so far: <b>${escapeHtml(money(year.owed))}</b>${
      year.owedOnPace == null
        ? ""
        : `, on pace for ${escapeHtml(money(year.owedOnPace))}`
    }`,
  );
  p(escapeHtml(paceSentence(year)));

  if (month.crews.length > 0) {
    h(`By crew — ${name}`);
    for (const crew of month.crews) {
      out.push(
        '<div style="border-left:3px solid #e2e2e2;padding:2px 0 2px 10px;margin:0 0 10px">',
        `<div style="font-weight:600">${escapeHtml(
          owedCrewName(crew.crew),
        )} — paid ${escapeHtml(money(crew.paid))}</div>`,
        '<div style="font-size:13px">' +
          `comp ${escapeHtml(money(crew.workersComp))} (${escapeHtml(
            standingWords(crew.wcStanding, "comp"),
          )}) &middot; liability ${escapeHtml(
            money(crew.generalLiability),
          )} (${escapeHtml(standingWords(crew.glStanding, "liability"))})</div>`,
      );
      for (const row of crew.orders) {
        out.push(
          '<div style="color:#555;font-size:13px;margin-top:4px">' +
            `WO-${row.order.order_number ?? "?"} &middot; ${escapeHtml(
              owedJobLabel(row.order),
            )} &middot; ` +
            `paid ${escapeHtml(shortDate(row.paidOn))} &middot; ${escapeHtml(
              money(row.paid),
            )}</div>`,
          '<div style="color:#555;font-size:13px;padding-left:14px">' +
            `${escapeHtml(owedRateLine(row))}</div>`,
        );
      }
      out.push("</div>");
    }
  }

  out.push(
    '<p style="margin:16px 0 0;color:#666;font-size:13px">Certificates are read ' +
      "as they stand today, so a crew whose certificate has run out is charged " +
      "as having none.</p>",
    `<p style="margin:14px 0 0"><a href="${escapeHtml(options.url)}">` +
      "Open it in Roof Board</a></p>",
    "</div>",
  );
  return out.join("\n");
}
